import { useState, useEffect, useCallback } from 'react';
import { Modal, View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTheme } from '@/contexts/ThemeContext';
import { Dumbbell, BarChart3, Download, Swords, X } from 'lucide-react-native';

const SEEN_KEY = '@duel_welcome_pro_seen';

const UNLOCKED = [
  { icon: Dumbbell, label: 'DUEL Program', desc: 'Personalized weekly training plans' },
  { icon: BarChart3, label: 'Advanced Analytics', desc: 'Score history & volume trends' },
  { icon: Download, label: 'Data Export', desc: 'CSV export of your workout history' },
  { icon: Swords, label: 'Unlimited Duels', desc: 'No monthly cap on challenges' },
];

interface Props {
  visible: boolean;
  onDismiss: () => void;
}

export default function WelcomeProModal({ visible, onDismiss }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;

  const handleDismiss = async () => {
    await AsyncStorage.setItem(SEEN_KEY, 'true');
    onDismiss();
  };

  return (
    <Modal visible={visible} animationType="fade" transparent statusBarTranslucent>
      <View style={[st.overlay]}>
        <View style={[st.card, { backgroundColor: c.surface, borderColor: c.accent }]}>
          <View style={[st.topBar, { borderBottomColor: c.border }]}>
            <Text style={[st.title, { color: c.accent }]}>WELCOME TO PRO</Text>
            <TouchableOpacity onPress={handleDismiss} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
              <X size={18} color={c.textSecondary} />
            </TouchableOpacity>
          </View>
          <ScrollView contentContainerStyle={st.body} showsVerticalScrollIndicator={false}>
            <Text style={[st.sub, { color: c.textSecondary }]}>
              {"Here's what you've unlocked:"}
            </Text>
            {UNLOCKED.map(({ icon: Icon, label, desc }) => (
              <View key={label} style={[st.row, { borderColor: c.border }]}>
                <View style={[st.iconWrap, { backgroundColor: c.accent + '18' }]}>
                  <Icon size={18} color={c.accent} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[st.rowLabel, { color: c.text }]}>{label}</Text>
                  <Text style={[st.rowDesc, { color: c.textSecondary }]}>{desc}</Text>
                </View>
              </View>
            ))}
          </ScrollView>
          <View style={[st.footer, { borderTopColor: c.border }]}>
            <TouchableOpacity style={[st.cta, { backgroundColor: c.accent }]} onPress={handleDismiss} activeOpacity={0.85}>
              <Text style={[st.ctaText, { color: (c as any).accentForeground ?? '#030303' }]}>{"LET'S GO"}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/**
 * Hook: controls visibility of the WelcomeProModal.
 * Shows once when user has Pro tier and hasn't seen it yet.
 */
export function useWelcomeProModal(isPro: boolean): {
  showWelcomePro: boolean;
  markWelcomeProSeen: () => void;
} {
  const [showWelcomePro, setShowWelcomePro] = useState(false);

  useEffect(() => {
    if (!isPro) return;
    AsyncStorage.getItem(SEEN_KEY)
      .then((seen) => {
        if (!seen) setShowWelcomePro(true);
      })
      .catch(() => {});
  }, [isPro]);

  const markWelcomeProSeen = useCallback(() => {
    setShowWelcomePro(false);
  }, []);

  return { showWelcomePro, markWelcomeProSeen };
}

const st = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.85)', justifyContent: 'center', alignItems: 'center', padding: 24 },
  card: { width: '100%', maxWidth: 400, borderWidth: 1, borderRadius: 4, overflow: 'hidden' },
  topBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingVertical: 16, borderBottomWidth: 1 },
  title: { fontFamily: 'Inter_700Bold', fontSize: 13, letterSpacing: 3 },
  body: { padding: 20, gap: 12 },
  sub: { fontFamily: 'Inter_400Regular', fontSize: 13, marginBottom: 4 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 14, paddingVertical: 12, borderBottomWidth: 1 },
  iconWrap: { width: 38, height: 38, borderRadius: 4, alignItems: 'center', justifyContent: 'center' },
  rowLabel: { fontFamily: 'Inter_600SemiBold', fontSize: 14 },
  rowDesc: { fontFamily: 'Inter_400Regular', fontSize: 12, marginTop: 1 },
  footer: { padding: 16, borderTopWidth: 1 },
  cta: { paddingVertical: 14, borderRadius: 2, alignItems: 'center' },
  ctaText: { fontFamily: 'Inter_700Bold', fontSize: 13, letterSpacing: 3 },
});
